import React, { useMemo } from 'react';
import { MessageSquare, Megaphone, ChevronRight } from 'lucide-react';
import { Agent } from '../types';
import { ConversationSummary } from '../services/conversationHistoryService';
import { SCOUT_CONTAINER } from './scoutLayout';
import { trackHomeEvent } from '../services/homeTelemetry';

interface HomeActivityRowProps {
  agents: Agent[];
  conversations?: ConversationSummary[];
  onOpenConversation?: (conversationId: string) => void;
  onStartConversation: (agentId: string) => void;
}

const RECENT_LIMIT = 4;
const UPDATES_LIMIT = 4;

/** "10:24 AM" today, "Yesterday", then "Sep 23". */
export function formatWhen(iso: string, now: Date = new Date()): string {
  // A bare "2026-07-15" parses as UTC midnight, which shows as the 14th anywhere in the Americas.
  // Read date-only values as local dates.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  if (date.getTime() >= startOfToday) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  if (date.getTime() >= startOfToday - dayMs) return 'Yesterday';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export interface AgentUpdate {
  agentId: string;
  agentName: string;
  version: string;
  date: string;
  summary: string;
}

/**
 * The newest version-history entries across the agents this user can see, newest first. This is real
 * release information from agents.json, which is why the panel is titled "What's new" rather than
 * presenting it as company announcements.
 */
export function latestAgentUpdates(agents: Agent[], limit = UPDATES_LIMIT): AgentUpdate[] {
  return agents
    .filter(agent => !agent.hidden)
    .flatMap(agent => (agent.versionHistory || []).map(entry => ({
      agentId: agent.id,
      agentName: agent.name,
      version: entry.version,
      date: entry.date,
      // Notes are written as "first change; second change". The first clause is the headline.
      summary: (entry.notes || '').split(/[;\n]/)[0].trim() || `Version ${entry.version} released`,
    })))
    .filter(update => update.date)
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, limit);
}

const Panel: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <section className="rounded-2xl bg-scout-surface dark:bg-scout-surface-dark border border-scout-line dark:border-scout-line-dark shadow-scout-card">
    <h2 className="flex items-center gap-2 px-5 pt-4 pb-2 text-sm font-semibold text-scout-ink dark:text-white">
      <span className="text-scout-ink-3">{icon}</span>
      {title}
    </h2>
    {children}
  </section>
);

const HomeActivityRow: React.FC<HomeActivityRowProps> = ({
  agents,
  conversations = [],
  onOpenConversation,
  onStartConversation,
}) => {
  const recent = useMemo(() => {
    const unique = new Map<string, ConversationSummary>();
    [...conversations]
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .forEach(conversation => {
        if (!unique.has(conversation.id)) unique.set(conversation.id, conversation);
      });
    return Array.from(unique.values()).slice(0, RECENT_LIMIT);
  }, [conversations]);

  const updates = useMemo(() => latestAgentUpdates(agents), [agents]);

  if (!onOpenConversation && updates.length === 0) {
    return null;
  }

  return (
    <div className={`${SCOUT_CONTAINER} pb-10 grid gap-4 grid-cols-1 lg:grid-cols-2`}>
      {onOpenConversation && (
        <Panel icon={<MessageSquare className="w-4 h-4" />} title="Recent with Scout">
          {recent.length === 0 ? (
            <p className="px-5 pb-5 pt-1 text-sm text-scout-ink-3">
              Your conversations will appear here once you ask Scout something.
            </p>
          ) : (
            <ul className="px-2 pb-2">
              {recent.map((conversation, index) => (
                <li key={conversation.id}>
                  <button
                    type="button"
                    onClick={() => {
                      trackHomeEvent({ name: 'HomeRecentConversationOpened', properties: {
                        agentName: conversation.agentName || 'unknown',
                        position: index + 1,
                      } });
                      onOpenConversation(conversation.id);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-scout-canvas dark:hover:bg-scout-canvas-dark transition-colors"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-scout-ink dark:text-gray-100 truncate">
                        {conversation.title}
                      </span>
                      {conversation.agentName && (
                        <span className="block text-xs text-scout-ink-3 truncate">{conversation.agentName}</span>
                      )}
                    </span>
                    <span className="text-xs text-scout-ink-3 tabular-nums shrink-0">
                      {formatWhen(conversation.updatedAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}

      {updates.length > 0 && (
        <Panel icon={<Megaphone className="w-4 h-4" />} title="What's new">
          <ul className="px-2 pb-2">
            {updates.map((update, index) => (
              <li key={`${update.agentId}-${update.version}`}>
                <button
                  type="button"
                  onClick={() => {
                    trackHomeEvent({ name: 'HomeWhatsNewClicked', properties: {
                      agentId: update.agentId,
                      version: update.version,
                      position: index + 1,
                    } });
                    onStartConversation(update.agentId);
                  }}
                  className="w-full flex items-start gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-scout-canvas dark:hover:bg-scout-canvas-dark transition-colors group"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-scout-ink dark:text-gray-100">
                      <span className="font-medium">{update.agentName}</span>
                      <span className="ml-1.5 text-xs text-scout-ink-3 tabular-nums">v{update.version}</span>
                    </span>
                    <span className="block text-xs text-scout-ink-2 dark:text-gray-400 line-clamp-1">
                      {update.summary}
                    </span>
                  </span>
                  <span className="flex items-center gap-1 text-xs text-scout-ink-3 tabular-nums shrink-0 pt-0.5">
                    {formatWhen(update.date)}
                    <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
};

export default HomeActivityRow;
